/* eslint-disable max-len */
import { Injectable } from '@angular/core';
import { User } from '@core/models/user.model';
import { SetNotificationAction } from '@core/store/notification/notification.actions';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { AlertDialogType } from '@shared/models';
import { Pagination } from '@shared/models/pagination.model';
import { EntityMap } from '@shared/models/types';
import { FileHandlerService } from '@shared/services/file-handler.service';
import { DialogHandlerService } from '@core/services/dialog-handler.service';
import { JobGroup } from 'app/modules/job-group/models/job-group.model';
import { catchError, filter, map, mergeMap, tap, timeout, switchMap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { DashboardStateModel } from '../models/state/dashboard-state.model';
import { TaskFeedSummary } from '../models/state/task-feed-summary.model';
import { DashBoardService } from '../services/dashboard.service';
import { CreateJobFile, CreateJobGroup, FetchInternalCollaborators, FetchOngoingJobs, FetchSelectedOngoingJob, FetchTaskFeedSummary, NavigateToJobFile, ProcessOrgLogoUrl, SearchJobGroupsForJobFileCreation, FetchCustomers, UpdateDashboardRows, FetchRevenueFinancialData, FetchCostFinancialData, FetchProfitFinancialData, WorkOrderNotificationsAction, CleanupWorkOrderNotificationsAction, ClearDashboardStateAction, FetchSupplierTrips, FetchCustomerTrips, FetchCustomerKilometers, JobSummaryAction, UpdateJobWorkOrders, FetchCostPerKm, FetchCostPerShipment, FetchRevenuePerKm, FetchRevenuePerShipment, FetchProfitPerKm, FetchProfitPerShipment, FetchMostProfitableCustomers, FetchVehicleUtilizationMetrics, SetVehicleUtilizationMetrics, UpdateJobSummaryUIState, RestoreJobSummaryUIState, FetchSupplierCostAnalysis, FetchSupplierList, FetchSupplierTimeSeries, FetchOnlineOfflineDeviceCount, FetchTimeInsightsData, FetchBehalfPartnerOrganizations, FetchBehalfOnboardedOrganizations, FetchBehalfOrgDetails, CreateHaulagePlan, NavigateToHaulagePlanWizard, CreateOperation } from './dash-board.actions';
import { Operation } from 'app/modules/organization/models/operation';
import { AddOperationToTop } from '@core/store/auth/auth.actions';
import { SetSkeletonLoader, SetSpinner } from '@core/store/shared.actions';
import { OnGoingJob } from '../models/on-going-job.model';
import { JobGroupState } from 'app/modules/job-group/store/job-group.state';
import { SelectJobGroup, SetSelectedJobGroupForDashboardAfterCreation } from 'app/modules/job-group/store/job-group.actions';
import { DASHBOARD_ROWS_EXAMPLE, DASHBOARD_CUSTOMERS_EXAMPLE } from '../models/state/dashboard-state.model';
import { Customer } from '../models/customer.model';
import { FirebaseService } from '@core/services/firebase.service';
import { NotificationModel } from '@control-tower/models/notification.model';
import { FinancialType, ValuePerType } from '../models/enums/financial-type.enum';
import { PeriodType } from '../models/enums/period-type.enum';
import { InvoiceFinancialResult } from '../models/invoice-financial.model';
import { SupplierTripsResponse } from '../models/supplier-trips.model';
import { CompletedJobItem, DeliveryInProgressJobItem, JobStatusEnum, JobSummaryTimeRange, NotifiedJobItem, ContractedJobItem, PlannedJobItem, TimeRangeEnum, LoadAssignmentJobItem, PendingAcceptanceJobItem, AcceptedJobItem, TerminatedJobItem } from '../models/job-summary.model';
import { EMPTY, Subscription, from, timer } from 'rxjs';
import { of, throwError } from 'rxjs';
import { concatMap } from 'rxjs/operators';
import { JobSummaryRequest } from '../models/job-summary.model';
import { JobSummaryPageCache, jobSummaryRequestKey, otherTabFirstPages } from '../utils/job-summary-page-cache.util';

/** Wait after a tab is shown before fetching the other tabs, so the shown tab's rendering goes first. */
const JOB_SUMMARY_PREFETCH_DELAY_MS = 300;
import { WorkOrder } from '../../control-tower/models/work-order/work-order';
import { CustomerTripsResponse } from '../models/customer-trips.model';
import { CustomerKilometersResponse } from '../models/customer-kilometers.model';
import { OrgDetails } from '@shared/models/org-details.model';
import { CustomerNodeTreeMap } from '../models/customer-most-profiyable';
import { VehicleUtilizationMetric } from '../models/vehicle-utilization-metric.model';
import { SupplierCostAnalysisResponse } from '../models/supplier-costs.model';
import { SupplierListResponse, SupplierTimeSeriesResponse } from '../models/supplier-list.model';
import { TimeInsightsResult } from '../models/time-insights.model';

export const dashboardStateKey = 'dashboard';
export const getDashboardState = (): DashboardStateModel => (
  {
    onGoingJobs: {},
    taskFeedSummary: {todosCount: 0, followUpsCount: 0},
    onGoingJobsPaginationData: {
      pageNumber: 0,
      pageSize: 10,
      totalElements: 0
    },

    logos: {},
    internalCollaborators: [],
    jobGroupsForJobFileCreation: {},
    selectedOnGoingJob: null,
    dashboardRows: DASHBOARD_ROWS_EXAMPLE,
    customers: [],
    jobSummary: {
      completedJobs: [],
      deliveryInProgressJobs: [],
      notifiedJobs: [],
      contractedJobs: [],
      plannedJobs: [],
      loadAssignmentJobs: [],
      pendingAcceptanceJobs: [],
      acceptedJobs: [],
      terminatedJobs: [],
      timeZone: null,
      pagination: {
        totalItems: 0,
        itemsPerPage: 10,
        currentPage: 0,
        totalPages: 0
      },
      timeRange: {
        startDate: '',
        endDate: ''
      },
      selectedStatus: JobStatusEnum.planned,
      loading: true,
      error: null,
      uiState: {
        activeTab: JobStatusEnum.planned,
        timeRange: TimeRangeEnum.hours24,
        dateRange: {
          startDate: new Date(new Date().getTime() - 24 * 60 * 60 * 1000), // 24 hours ago
          endDate: new Date()
        },
        pageIndex: 0,
        pageSize: 10,
        sortColumn: null,
        sortDirection: ''
      }
    },
    supplierTrips: null,
    supplierCostAnalysis: null,
    supplierList: null,
    supplierTimeSeries: null,
    customerTrips: null,
    mostProfitableCustomers: [],
    customerKilometers: null,
    vehicleUtilizationMetrics: [],
    partnerOrganizations: [],
    onboardedOrganizations: [],
    behalfOrgDetails: {},
    newlyCreatedOperationId: null
  });

@State({
  name: dashboardStateKey,
  defaults: getDashboardState()
})

@Injectable()
export class DashboardState {
  private workOrderNotificationSubscriptions: Subscription[] = [];

  constructor(private store: Store,
    private dashboardService: DashBoardService,
    private fileHandlerService: FileHandlerService,
    private firebaseService: FirebaseService,
    private dialogHandlerService: DialogHandlerService
  ) {}

  @Selector()
  static getTaskFeedSummary(state: DashboardStateModel): TaskFeedSummary {
    return state.taskFeedSummary;
  }

  @Selector()
  static getOnGoingJobs(state: DashboardStateModel): any[] {
    return Object.values(state.onGoingJobs);
  }

  @Selector()
  static getOnGoingJobsPaginationData(state: DashboardStateModel): Pagination {
    return state.onGoingJobsPaginationData;
  }

  @Selector()
  static getJobFileInternalCollaborator(state: DashboardStateModel): User[]{
    return state.internalCollaborators;
  }

  @Selector()
  static getLogosMap(state: DashboardStateModel): EntityMap<string, string> {
    return state.logos;
  }

  @Selector()
  static getJobGroupsForJobFileCreation(state: DashboardStateModel): JobGroup[]{
    return Object.values(state.jobGroupsForJobFileCreation).sort((a, b) => {
      // Keep 'default' job group at the end
      if (a.id === 'default') return 1;
      if (b.id === 'default') return -1;
      
      // Sort by createdTimestamp descending (newest first)
      // If createdTimestamp is missing, treat as oldest (put before default)
      const aTime = a.createdTimestamp ? new Date(a.createdTimestamp).getTime() : 0;
      const bTime = b.createdTimestamp ? new Date(b.createdTimestamp).getTime() : 0;
      return bTime - aTime;
    });
  }

  @Selector()
  static getSelectedOnGoingJob(state: DashboardStateModel): OnGoingJob {
    return state.selectedOnGoingJob;
  }

  @Selector()
  static getCustomers(state: DashboardStateModel): Customer[] {
    return state.customers;
  }

  @Selector()
  static getRevenueFinancialData(state: DashboardStateModel): InvoiceFinancialResult {
    return state.revenueFinancialData;
  }

  @Selector()
  static getCostFinancialData(state: DashboardStateModel): InvoiceFinancialResult {
    return state.costFinancialData;
  }

  @Selector()
  static getProfitFinancialData(state: DashboardStateModel): InvoiceFinancialResult {
    return state.profitFinancialData;
  }

  @Selector()
  static getSupplierTrips(state: DashboardStateModel): SupplierTripsResponse {
    return state.supplierTrips;
  }

  @Selector()
  static getSupplierCostAnalysis(state: DashboardStateModel): SupplierCostAnalysisResponse {
    return state.supplierCostAnalysis;
  }

  @Selector()
  static getCustomerTrips(state: DashboardStateModel): CustomerTripsResponse {
    return state.customerTrips;
  }

  @Selector()
  static getCostPerKm(state: DashboardStateModel) {
    return state.costPerKm;
  }

  @Selector()
  static getCostPerShipment(state: DashboardStateModel) {
    return state.costPerShipment;
  }

  @Selector()
  static getRevenuePerKm(state: DashboardStateModel) {
    return state.revenuePerKm;
  }

  @Selector()
  static getRevenuePerShipment(state: DashboardStateModel) {
    return state.revenuePerShipment;
  }

  @Selector()
  static getProfitPerKm(state: DashboardStateModel) {
    return state.profitPerKm;
  }

  @Selector()
  static getProfitPerShipment(state: DashboardStateModel) {
    return state.profitPerShipment;
  }

  @Selector()
  static getCustomerKilometers(state: DashboardStateModel): CustomerKilometersResponse {
    return state.customerKilometers;
  }

  @Selector()
  static getCompletedJobs(state: DashboardStateModel): CompletedJobItem[] {
    return state.jobSummary.completedJobs;
  }

  @Selector()
  static getDeliveryInProgressJobs(state: DashboardStateModel): DeliveryInProgressJobItem[] {
    return state.jobSummary.deliveryInProgressJobs;
  }

  @Selector()
  static getNotifiedJobs(state: DashboardStateModel): NotifiedJobItem[] {
    return state.jobSummary.notifiedJobs;
  }

  @Selector()
  static getContractedJobs(state: DashboardStateModel): ContractedJobItem[] {
    return state.jobSummary.contractedJobs;
  }

  @Selector()
  static getPlannedJobs(state: DashboardStateModel): PlannedJobItem[] {
    return state.jobSummary.plannedJobs;
  }

  @Selector()
  static getLoadAssignmentJobs(state: DashboardStateModel): LoadAssignmentJobItem[] {
    return state.jobSummary.loadAssignmentJobs || [];
  }

  @Selector()
  static getPendingAcceptanceJobs(state: DashboardStateModel): PendingAcceptanceJobItem[] {
    return state.jobSummary.pendingAcceptanceJobs || [];
  }

  @Selector()
  static getAcceptedJobs(state: DashboardStateModel): AcceptedJobItem[] {
    return state.jobSummary.acceptedJobs || [];
  }

  @Selector()
  static getTerminatedJobs(state: DashboardStateModel): TerminatedJobItem[] {
    return state.jobSummary.terminatedJobs || [];
  }

  /** Tenant timezone for Job Summary dates. */
  @Selector()
  static getJobSummaryTimeZone(state: DashboardStateModel): string | null {
    return state.jobSummary.timeZone ?? null;
  }

  @Selector()
  static getTimeRange(state: DashboardStateModel): JobSummaryTimeRange {
    return state.jobSummary.timeRange;
  }

  @Selector()
  static getSelectedStatus(state: DashboardStateModel): JobStatusEnum {
    return state.jobSummary.selectedStatus;
  }

  @Selector()
  static getLoading(state: DashboardStateModel): boolean {
    return state.jobSummary.loading;
  }

  @Selector()
  static getError(state: DashboardStateModel): string | null {
    return state.jobSummary.error;
  }

  @Selector()
  static getWorkOrdersForJob(state: DashboardStateModel) {
    return (jobRefId: string): WorkOrder[] => {
      // Search through all job types for the specific job
      const allJobs = [
        ...state.jobSummary.completedJobs,
        ...state.jobSummary.deliveryInProgressJobs,
        ...state.jobSummary.notifiedJobs,
        ...state.jobSummary.contractedJobs,
        ...state.jobSummary.plannedJobs,
        ...(state.jobSummary.loadAssignmentJobs || []),
        ...(state.jobSummary.pendingAcceptanceJobs || []),
        ...(state.jobSummary.acceptedJobs || []),
        ...(state.jobSummary.terminatedJobs || [])
      ];

      const foundJob = allJobs.find(jobItem => jobItem.jobReferenceId === jobRefId);
      return foundJob?.allWorkOrders || [];
    };
  }

  @Selector()
  static getAllWorkOrdersFromJobs(state: DashboardStateModel): WorkOrder[] {
    const allJobs = [
      ...state.jobSummary.completedJobs,
      ...state.jobSummary.deliveryInProgressJobs,
      ...state.jobSummary.notifiedJobs,
      ...state.jobSummary.contractedJobs,
      ...state.jobSummary.plannedJobs,
      ...(state.jobSummary.loadAssignmentJobs || []),
      ...(state.jobSummary.pendingAcceptanceJobs || []),
      ...(state.jobSummary.acceptedJobs || []),
      ...(state.jobSummary.terminatedJobs || [])
    ];

    return allJobs.reduce((allWorkOrders: WorkOrder[], jobItem) => {
      if (jobItem.allWorkOrders) {
        allWorkOrders.push(...jobItem.allWorkOrders);
      }
      return allWorkOrders;
    }, []);
  }

  @Selector()
  static getMostProfitableCustomers(state: DashboardStateModel): CustomerNodeTreeMap[] {
    return state.mostProfitableCustomers || [];
  }

  @Selector()
  static getVehicleUtilizationMetrics(state: DashboardStateModel): VehicleUtilizationMetric[] {
    return state.vehicleUtilizationMetrics;
  }

  // New selectors for UI state
  @Selector()
  static getJobSummaryUIState(state: DashboardStateModel) {
    return state.jobSummary.uiState;
  }

  @Selector()
  static getUIActiveTab(state: DashboardStateModel): JobStatusEnum {
    return state.jobSummary.uiState.activeTab;
  }

  @Selector()
  static getUITimeRange(state: DashboardStateModel): TimeRangeEnum {
    return state.jobSummary.uiState.timeRange;
  }

  @Selector()
  static getUIDateRange(state: DashboardStateModel) {
    return state.jobSummary.uiState.dateRange;
  }

  @Selector()
  static getUIPageIndex(state: DashboardStateModel): number {
    return state.jobSummary.uiState.pageIndex;
  }

  @Selector()
  static getUIPageSize(state: DashboardStateModel): number {
    return state.jobSummary.uiState.pageSize;
  }

  @Selector()
  static getUISortColumn(state: DashboardStateModel): string | null {
    return state.jobSummary.uiState.sortColumn;
  }

  @Selector()
  static getUISortDirection(state: DashboardStateModel): 'asc' | 'desc' | '' {
    return state.jobSummary.uiState.sortDirection;
  }

  @Selector()
  static getSupplierList(state: DashboardStateModel): SupplierListResponse {
    return state.supplierList;
  }

  @Selector()
  static getSupplierTimeSeries(state: DashboardStateModel): SupplierTimeSeriesResponse {
    return state.supplierTimeSeries;
  }

  @Selector()
  static getOnlineOfflineDeviceCount(state: DashboardStateModel): { online: number; offline: number } {
    return state.onlineOfflineDeviceCount;
  }

  @Selector()
  static getPartnerOrganizations(state: DashboardStateModel): any[] {
    return state.partnerOrganizations;
  }

  @Selector()
  static getOnboardedOrganizations(state: DashboardStateModel): any[] {
    return state.onboardedOrganizations;
  }

  @Selector()
  static getBehalfOrgDetails(state: DashboardStateModel): EntityMap<string, { logo: string; orgName: string }> {
    return state.behalfOrgDetails;
  }

  @Selector()
  static getBehalfOrgDetailsByOrgId(state: DashboardStateModel) {
    return (orgId: string) => state.behalfOrgDetails[orgId];
  }

  @Selector()
  static getNewlyCreatedOperationId(state: DashboardStateModel): string | null {
    return state.newlyCreatedOperationId;
  }

  @Action(CreateOperation)
  createOperation({ dispatch, patchState, getState }: StateContext<DashboardStateModel>, { operation }: CreateOperation) {
    // Validate operation name before API call
    if (!operation.name || operation.name.trim() === '') {
      return dispatch(new SetNotificationAction('Operation name is required', AlertDialogType.ERROR));
    }

    // Check for duplicate operation name in current state
    const currentOperations = this.store.selectSnapshot(state => state.auth.currentUserOperations) || [];
    const operationNameLower = operation.name.trim().toLowerCase();
    const duplicateOperation = currentOperations.find(
      op => op.name && op.name.trim().toLowerCase() === operationNameLower
    );
    
    if (duplicateOperation) {
      return dispatch(new SetNotificationAction('An operation with this name already exists. Please choose a different name.', AlertDialogType.ERROR));
    }

    return this.dashboardService.createOperation(operation)
      .pipe(
        timeout(30000), // 30 second timeout
        catchError((err: HttpErrorResponse | Error) => {
          let errorMessage = 'Unable to create operation. Please try again.';
          
          // Handle timeout errors
          if (err instanceof Error && err.name === 'TimeoutError') {
            errorMessage = 'Operation creation timed out. Please check your connection and try again.';
            return dispatch(new SetNotificationAction(errorMessage, AlertDialogType.ERROR));
          }
          
          // Handle HTTP errors
          if (err instanceof HttpErrorResponse) {
            const status = err.status;
            const errorBody = err.error;
            
            // Check for duplicate operation name from server (typically 409 Conflict or 400 with specific message)
            // Also check various possible error message formats
            const errorMessageText = errorBody?.message || errorBody?.error || '';
            const errorMessageLower = typeof errorMessageText === 'string' ? errorMessageText.toLowerCase() : '';
            
            if (status === 409 || 
                (status === 400 && (
                  errorMessageLower.includes('duplicate') ||
                  errorMessageLower.includes('already exists') ||
                  errorMessageLower.includes('name must be unique') ||
                  errorMessageLower.includes('operation name') && errorMessageLower.includes('exists')
                ))) {
              errorMessage = 'An operation with this name already exists. Please choose a different name.';
            }
            // Check for validation errors (400 Bad Request)
            else if (status === 400) {
              // Display server validation messages if available
              if (errorBody?.message) {
                errorMessage = errorBody.message;
              } else if (errorBody?.error) {
                errorMessage = typeof errorBody.error === 'string' 
                  ? errorBody.error 
                  : 'Validation failed. Please check your input.';
              } else {
                errorMessage = 'Validation failed. Please check your input.';
              }
            }
            // Check for network errors
            else if (status === 0 || status === 504 || status === 503) {
              errorMessage = 'Operation creation timed out. Please check your connection and try again.';
            }
            // Other server errors
            else if (status >= 500) {
              errorMessage = 'Unable to create operation. Please try again.';
            }
          }
          
          return dispatch(new SetNotificationAction(errorMessage, AlertDialogType.ERROR));
        }),
        tap((createdOperation: Operation) => {
          patchState({
            newlyCreatedOperationId: createdOperation.id || null
          });
        }),
        mergeMap((createdOperation: Operation) => {
          return dispatch([
            new AddOperationToTop(createdOperation),
            new SetNotificationAction('Operation Created Successfully', AlertDialogType.SUCCESS),
          ]);
        })
      );
  }

  @Action(FetchOngoingJobs)
  fetchOnGoingJobs({ dispatch, getState, patchState }: StateContext<DashboardStateModel>, {pageNumber, pageSize, searchText}: FetchOngoingJobs) {
    const {user: {orgId, id}} = this.store.selectSnapshot(state => state.auth);
    return this.dashboardService.fetchOngoingJobs(orgId, id, pageNumber, pageSize, !!searchText ? searchText : undefined )
      .pipe(
        map(res => {
          dispatch(new SetSkeletonLoader(false));
          return patchState({
            onGoingJobs: res.content.reduce((prev, curr) => {
              prev[curr.jobRefId] = curr;
              return prev;
            }, {}),
            onGoingJobsPaginationData: {
              pageNumber: res.pageable.pageNumber,
              pageSize: res.pageable.pageSize,
              totalElements: res.totalElements
            }
          });
        }),
        catchError((err) => dispatch([new SetSpinner(false), new SetNotificationAction('Job Group Creation Failed', AlertDialogType.ERROR)])),
      );
  }

  @Action(FetchSelectedOngoingJob)
  fetchSelectedOnGoingJob({ patchState }: StateContext<DashboardStateModel>, {jobRefId}: FetchSelectedOngoingJob) {
    const {user: {orgId}} = this.store.selectSnapshot(state => state.auth);
    return this.dashboardService.fetchTaskFeedJobById(orgId, jobRefId)
      .pipe(
        map(selectedOnGoingJob => patchState({
          selectedOnGoingJob
        }))
      );
  }

  @Action(FetchTaskFeedSummary)
  fetchTaskFeedSummary({ patchState }: StateContext<DashboardStateModel>) {
    const {user: {orgId, userId}} = this.store.selectSnapshot(state => state.auth);
    return this.dashboardService.fetchTaskFeedSummary(orgId, userId)
      .pipe(
        map(summary => patchState({
          taskFeedSummary: {
            todosCount: summary.todosCount,
            followUpsCount: summary.followUpsCount
          }
        }))
      );
  }

  @Action(CreateJobFile)
  createJobFile({ dispatch }: StateContext<DashboardStateModel>, { jobFileCreationModel }: CreateJobFile) {
    const {user: {orgId}} = this.store.selectSnapshot(state => state.auth);
    const customerId = jobFileCreationModel.customerId !== null ? jobFileCreationModel.customerId : orgId;
    return this.dashboardService.createJobFile({...jobFileCreationModel, orgId})
      .pipe(
        mergeMap((res) => dispatch([
          new SetNotificationAction('Job File Created Successfully', AlertDialogType.SUCCESS),
          new NavigateToJobFile({...res, customerId})
        ])),
        catchError((_) => dispatch(new SetNotificationAction('Job File Creation Failed', AlertDialogType.ERROR)))
      );
  }

  @Action(CreateHaulagePlan)
  createHaulagePlan({ dispatch }: StateContext<DashboardStateModel>,
                    { haulagePlanCreationModel, planningMode, shipmentOrderSpecIds }: CreateHaulagePlan) {
    const {user: {orgId}} = this.store.selectSnapshot(state => state.auth);
    return this.dashboardService.createJobFileV2({...haulagePlanCreationModel, orgId})
      .pipe(
        // When the flow started from Order Manager, seed the new job with the selected orders
        // before navigating, so the wizard opens with them already attached. With no ids this is
        // a no-op and the behaviour is exactly as it was.
        mergeMap((res) => (shipmentOrderSpecIds && shipmentOrderSpecIds.length
          ? this.dashboardService.mergeShipmentOrdersToJobByIds(orgId, res.jobRefId, shipmentOrderSpecIds)
              // The job already exists at this point, so a failure here is not a creation failure:
              // say what actually went wrong and still navigate, rather than leaving the user on the
              // dialog believing nothing was created.
              .pipe(catchError(() => dispatch(new SetNotificationAction(
                'Job created, but the selected orders could not be attached to it',
                AlertDialogType.ERROR))))
          : of(null)
        ).pipe(
          mergeMap(() => dispatch([
            new SetNotificationAction('Job File Created Successfully', AlertDialogType.SUCCESS),
            new NavigateToHaulagePlanWizard(res, planningMode)
          ]))
        )),
        catchError((_) => dispatch(new SetNotificationAction('Job File Creation Failed', AlertDialogType.ERROR)))
      );
  }

  @Action(ProcessOrgLogoUrl)
  processOrgLogoUrl({ dispatch, getState, patchState }: StateContext<DashboardStateModel>) {
    const { onGoingJobs } = getState();
    const orgIds = Array.from(new Set(Object.values(onGoingJobs)
      .filter(jf => jf.customer && jf.customer.orgId)
      .map(jf => jf.customer.orgId)));

    return this.dashboardService.getOrgDetails(orgIds)
      .pipe(
        tap(orgDetailsList => {
          const logos = {...getState().logos};
          orgDetailsList.forEach(orgDetails => {
            logos[orgDetails.orgId] = orgDetails.logo;
          });
          return patchState({
            logos
          });
        }),
        catchError((_) => dispatch(new SetNotificationAction('Error loading Logos', AlertDialogType.ERROR)))
      );
  }

  @Action(SearchJobGroupsForJobFileCreation)
  searchJobGroupsForJobFileCreation({ dispatch, patchState, getState }: StateContext<DashboardStateModel>, { searchText }: SearchJobGroupsForJobFileCreation) {
    const {user: {orgId}} = this.store.selectSnapshot(state => state.auth);

    const initialMap = !!searchText ? {default: {id: 'default', name: 'DEFAULT', orgId}} : {};
    return this.dashboardService.getJobGroups(orgId, 0, 20, searchText || '')
      .pipe(
        map((res) => patchState({
            jobGroupsForJobFileCreation: res.content.reduce((prev, curr) => {
              prev[curr.id] = curr;
              return prev;
            }, initialMap),
          })),
        catchError((_) => dispatch(new SetNotificationAction('Job Group Loading Failed', AlertDialogType.ERROR))),
      );
  }

  @Action(CreateJobGroup)
  createJobGroup({dispatch, patchState, getState}: StateContext<DashboardStateModel>, {name}: CreateJobGroup) {
    // Validate job group name before API call
    if (!name || name.trim() === '') {
      return dispatch(new SetNotificationAction('Job group name is required', AlertDialogType.ERROR));
    }

    const {user: {orgId}} = this.store.selectSnapshot(state => state.auth);
    
    // Helper function to create job group with error handling
    const createJobGroupWithErrorHandling = () => {
      return this.dashboardService.createJobGroup({ orgId, name }).pipe(
        timeout(30000),
        catchError((err: HttpErrorResponse | Error) => this.handleJobGroupCreationError(err, dispatch, orgId, name)),
        mergeMap((data: JobGroup) => this.handleJobGroupCreationSuccess(data, dispatch))
      );
    };

    // Check for duplicate job group name in current state
    const state = getState();
    const currentJobGroups = Object.values(state.jobGroupsForJobFileCreation) || [];
    const jobGroupNameLower = name.trim().toLowerCase();
    const duplicateJobGroup = currentJobGroups.find(
      jg => jg.name && jg.name.trim().toLowerCase() === jobGroupNameLower
    );
    
    // If duplicate found, show confirmation dialog
    if (duplicateJobGroup) {
      return this.showDuplicateConfirmationDialog(dispatch, orgId, name, createJobGroupWithErrorHandling);
    }

    // No duplicate, proceed directly
    return createJobGroupWithErrorHandling();
  }

  private handleJobGroupCreationError(
    err: HttpErrorResponse | Error,
    dispatch: (action: any) => any,
    orgId: string,
    name: string
  ): any {
    // Handle timeout errors
    if (err instanceof Error && err.name === 'TimeoutError') {
      return dispatch(new SetNotificationAction('Operation timed out. Please check your connection and try again.', AlertDialogType.ERROR));
    }
    
    // Handle HTTP errors
    if (err instanceof HttpErrorResponse) {
      const status = err.status;
      const errorBody = err.error;
      
      // Server detected duplicate (409), show confirmation dialog
      if (status === 409) {
        return this.showDuplicateConfirmationDialog(dispatch, orgId, name);
      }
      
      // Handle validation errors (400)
      if (status === 400) {
        const errorMessage = errorBody?.message || 
          (typeof errorBody?.error === 'string' ? errorBody.error : 'Validation failed. Please check your input.');
        return dispatch(new SetNotificationAction(errorMessage, AlertDialogType.ERROR));
      }
      
      // Handle network errors
      if (status === 0 || status === 504 || status === 503) {
        return dispatch(new SetNotificationAction('Operation timed out. Please check your connection and try again.', AlertDialogType.ERROR));
      }
      
      // Handle server errors (500+)
      if (status >= 500) {
        return dispatch(new SetNotificationAction('Unable to create job group. Please try again.', AlertDialogType.ERROR));
      }
    }
    
    // Default error
    return dispatch(new SetNotificationAction('Unable to create job group. Please try again.', AlertDialogType.ERROR));
  }

  private handleJobGroupCreationSuccess(data: JobGroup, dispatch: (action: any) => any): any {
    if (!data) return of(null);
    
    return dispatch([
      new SetNotificationAction('Job Group Created Successfully', AlertDialogType.SUCCESS),
      new SearchJobGroupsForJobFileCreation(),
      new SelectJobGroup(data.id),
      new SetSelectedJobGroupForDashboardAfterCreation(data.id, data)
    ]);
  }

  private showDuplicateConfirmationDialog(
    dispatch: (action: any) => any,
    orgId: string,
    name: string,
    createJobGroupCall?: () => any
  ): any {
    const title = 'Duplicate Job Group Name';
    const message = 'A Job Group with a similar name already exists. Do you want to proceed with creating this Job Group?';
    const dialogRef = this.dialogHandlerService.openConfirmationDialog(title, message, AlertDialogType.WARNING, false);
    
    return dialogRef.afterClosed().pipe(
      switchMap((confirmed: boolean) => {
        if (!confirmed) {
          return of(null);
        }
        
        // User confirmed, proceed with creation
        const createCall = createJobGroupCall || (() => {
          return this.dashboardService.createJobGroup({ orgId, name }).pipe(
            timeout(30000),
            catchError((err: HttpErrorResponse | Error) => this.handleJobGroupCreationError(err, dispatch, orgId, name)),
            mergeMap((data: JobGroup) => this.handleJobGroupCreationSuccess(data, dispatch))
          );
        });
        
        return createCall();
      })
    );
  }

  @Action(FetchInternalCollaborators)
  loadInternalCollaborators({dispatch, patchState, getState}: StateContext<DashboardStateModel>, {scopes, searchText, pageNum}: FetchInternalCollaborators) {

    const scopesForCollaborators = ['create:job-file', 'approve:job-file'];
    return this.dashboardService.fetchUsersByScopes(
      scopes && scopes.length > 0 ? scopes : scopesForCollaborators,
      pageNum || 0,
      20,
      searchText
    )
      .pipe(
        map(res => {
          const currentState = getState();
          // If pageNum is 0, replace the list (new search or initial load)
          // Otherwise, append to existing list
          const updatedCollaborators = pageNum === 0 
            ? res.content 
            : [...(currentState.internalCollaborators || []), ...res.content];
          
          return patchState({
            internalCollaborators: updatedCollaborators
          });
        }),
        catchError((_) => dispatch(new SetNotificationAction('Internal Collaborators Loading Failed', AlertDialogType.ERROR)))
      );
  }

  @Action(FetchCustomers)
  fetchCustomers({ patchState }: StateContext<DashboardStateModel>, { fromDate, toDate }: FetchCustomers) {
    const {orgId, operations} = this.store.selectSnapshot((state) => state.auth.user);
    return this.dashboardService.fetchHighestRevenuedCustomers(fromDate, toDate, orgId, operations.length === 1 ? operations[0] : null).pipe(
      mergeMap(customers => {
        const orgIds = customers.map(c => c.customerId);
        const totalRevenue = customers.reduce((sum, c) => sum + c.totalRevenue, 0);
        return this.dashboardService.getOrgDetailsLogos(orgIds).pipe(
          map(orgDetailsList => {
            const logoMap: EntityMap<string, OrgDetails> = orgDetailsList.reduce((acc, org) => {
              acc[org.orgId] = org;
              return acc;
            }, {});
            const customersWithLogos = customers.map(c => ({
              ...c,
              totalRevenue: c.totalRevenue,
              icon: logoMap[c.customerId]?.logo || 'assets/images/no_profile_image.png',
              name: logoMap[c.customerId]?.orgName || 'name',
            }));
            patchState({ customers: customersWithLogos });
          })
        );
      }),
      catchError(error => {
        console.log(error);
        return this.store.dispatch(
          new SetNotificationAction(`Failed to fetch customers`, AlertDialogType.ERROR)
        );
      })
    );
  }


  @Action(UpdateDashboardRows)
  updateDashboardRows({ patchState }: StateContext<DashboardStateModel>, { dashboardRows }: UpdateDashboardRows) {
    patchState({ dashboardRows });
  }

  @Action(WorkOrderNotificationsAction)
  initializeFirebaseNotifications({patchState, getState}: StateContext<DashboardStateModel>, {jobRefIds: loadIds}) {
    // Replace existing listeners instead of accumulating - prevents repeated Firestore Listen connections
    this.cleanupWorkOrderNotifications();

    if (!loadIds || loadIds.length === 0) {
      return;
    }

    const orgId = this.store.selectSnapshot((state) => state.auth.user.orgId);
    const sub = this.firebaseService.getNotificationsByContainerIdsPolling(orgId, loadIds, 30000).pipe(
      map(data => data.map(e => e.payload.doc.data() as NotificationModel)),
      filter(notifications => notifications.length > 0),
      tap(notifications => {

        notifications.forEach(notification => {
          if (notification.jobRefId) {
            // Map notification types to work order states
            let workOrderState = 'IN_PROGRESS'; // default state
            if (notification.updatedNotificationType === 'COMPLETED') {
              workOrderState = 'COMPLETED';
            } else if (notification.updatedNotificationType === 'CANCEL') {
              workOrderState = 'CANCEL';
            }

            const updatedWorkOrder = {
              id: notification.workOrderId,
              workOrderId: notification.workOrderId,
              jobRefId: notification.jobRefId,
              jpmId: notification.jpmId,
              legId: notification.legId,
              orgId: notification.orgId,
              containerId: notification.containerId,
              activityId: notification.activityId,
              updatedNotificationType: notification.updatedNotificationType,
              lastModifiedDate: new Date(),
              state: workOrderState
            };

            this.updateJobWorkOrderFromNotification(patchState, getState, notification.jobRefId, updatedWorkOrder);
            this.updateJobMetrics(patchState, getState, notification.jobRefId);
          }
        });
      })
    ).subscribe();
    this.workOrderNotificationSubscriptions.push(sub);
  }

  @Action(CleanupWorkOrderNotificationsAction)
  cleanupWorkOrderNotifications(): void {
    this.workOrderNotificationSubscriptions.forEach(sub => {
      if (sub && !sub.closed) {
        sub.unsubscribe();
      }
    });
    this.workOrderNotificationSubscriptions = [];
  }

  @Action(ClearDashboardStateAction)
  clearDashboardState(ctx: StateContext<DashboardStateModel>): void {
    this.cleanupWorkOrderNotifications();
    this.stopJobSummaryPrefetch();
    this.jobSummaryCache.clear();
    this.jobSummaryLatestKey = null;
    ctx.setState(getDashboardState());
  }

  @Action(UpdateJobWorkOrders)
  updateJobWorkOrders({patchState, getState}: StateContext<DashboardStateModel>, {jobRefId, workOrders}: UpdateJobWorkOrders) {
    this.updateJobWithWorkOrders(patchState, getState, jobRefId, workOrders);
    this.updateJobMetrics(patchState, getState, jobRefId);
  }

  @Action(FetchRevenueFinancialData)
  fetchRevenueFinancialData({ patchState, dispatch }: StateContext<DashboardStateModel>, { orgId, operationId, startDate, endDate, periodType }: FetchRevenueFinancialData) {
    const variables = {
      orgId,
      operationId,
      startDate,
      endDate,
      type: FinancialType.REVENUE,
      periodType: periodType || PeriodType.ONE_MONTH
    };
    return this.dashboardService.fetchInvoiceFinancialData(variables)
      .pipe(
        map((result) => {
          patchState({ revenueFinancialData: result });
          return result;
        }),
        catchError((err) => {
          console.error('Error fetching revenue financial data:', err);
          return dispatch(new SetNotificationAction('Failed to fetch revenue data', AlertDialogType.ERROR));
        })
      );
  }

  @Action(FetchCostFinancialData)
  fetchCostFinancialData({ patchState, dispatch }: StateContext<DashboardStateModel>, { orgId, operationId, startDate, endDate, periodType }: FetchCostFinancialData) {
    const variables = {
      orgId,
      operationId,
      startDate,
      endDate,
      type: FinancialType.COST,
      periodType: periodType || PeriodType.ONE_MONTH
    };
    return this.dashboardService.fetchInvoiceFinancialData(variables)
      .pipe(
        map((result) => {
          patchState({ costFinancialData: result });
          return result;
        }),
        catchError((err) => {
          console.error('Error fetching cost financial data:', err);
          return dispatch(new SetNotificationAction('Failed to fetch cost data', AlertDialogType.ERROR));
        })
      );
  }

  @Action(FetchProfitFinancialData)
  fetchProfitFinancialData({ patchState, dispatch }: StateContext<DashboardStateModel>, { orgId, operationId, startDate, endDate, periodType }: FetchProfitFinancialData) {
    const variables = {
      orgId,
      operationId,
      startDate,
      endDate,
      type: FinancialType.PROFIT,
      periodType: periodType || PeriodType.ONE_MONTH
    };
    return this.dashboardService.fetchInvoiceFinancialData(variables)
      .pipe(
        map((result) => {
          patchState({ profitFinancialData: result });
          return result;
        }),
        catchError((err) => {
          console.error('Error fetching profit financial data:', err);
          return dispatch(new SetNotificationAction('Failed to fetch profit data', AlertDialogType.ERROR));
        })
      );
  }

  @Action(FetchSupplierTrips)
  fetchSupplierTrips({ patchState }: StateContext<DashboardStateModel>, { filter: supplierTripFilter }: FetchSupplierTrips) {
    return this.dashboardService.fetchSupplierTrips(supplierTripFilter).pipe(
      mergeMap(response => {
        if (response?.supplierTrips && response.supplierTrips.length > 0) {
          const supplierIds = response.supplierTrips.map(s => s.supplierId);
          return this.dashboardService.getOrgDetailsLogos(supplierIds).pipe(
            map(orgDetailsList => {
              const logoMap: EntityMap<string, any> = orgDetailsList.reduce((acc, org) => {
                acc[org.orgId] = org;
                return acc;
              }, {});
              const supplierTripsWithLogos = {
                ...response,
                supplierTrips: response.supplierTrips.map(s => ({
                  ...s,
                  logo: logoMap[s.supplierId]?.logo || 'assets/images/no_profile_image.png'
                }))
              };
              patchState({ supplierTrips: supplierTripsWithLogos });
              return supplierTripsWithLogos;
            })
          );
        } else {
          patchState({ supplierTrips: response });
          return of(response);
        }
      }),
      catchError(error => {
        console.log(error);
        return this.store.dispatch(
          new SetNotificationAction(`Failed to fetch supplier trips: ${error.message}`, AlertDialogType.ERROR)
        );
      })
    );
  }

  @Action(FetchCustomerTrips)
  fetchCustomerTrips({ patchState }: StateContext<DashboardStateModel>, { filter: customerTripFilter }: FetchCustomerTrips) {
    return this.dashboardService.fetchCustomerTrips(customerTripFilter).pipe(
      mergeMap(response => {
        if (response?.customerTrips && response.customerTrips.length > 0) {
          const customerIds = response.customerTrips.map(c => c.customerId);
          return this.dashboardService.getOrgDetailsLogos(customerIds).pipe(
            map(orgDetailsList => {
              const logoMap: EntityMap<string, any> = orgDetailsList.reduce((acc, org) => {
                acc[org.orgId] = org;
                return acc;
              }, {});
              const customerTripsWithLogos = {
                ...response,
                customerTrips: response.customerTrips.map(c => ({
                  ...c,
                  logo: logoMap[c.customerId]?.logo || 'assets/images/no_profile_image.png'
                }))
              };
              patchState({ customerTrips: customerTripsWithLogos });
              return customerTripsWithLogos;
            })
          );
        } else {
          patchState({ customerTrips: response });
          return of(response);
        }
      }),
      catchError(error => {
        console.log(error);
        return this.store.dispatch(
          new SetNotificationAction(`Failed to fetch customer trips: ${error.message}`, AlertDialogType.ERROR)
        );
      })
    );
  }

  @Action(FetchCustomerKilometers)
  fetchCustomerKilometers({ patchState }: StateContext<DashboardStateModel>, { filter: customerKilometerFilter }: FetchCustomerKilometers) {
    return this.dashboardService.fetchCustomerKilometers(customerKilometerFilter).pipe(
      mergeMap(response => {
        if (response?.customerKilometers?.customers && response.customerKilometers.customers.length > 0) {
          const customerIds = response.customerKilometers.customers.map(c => c.customerId);
          return this.dashboardService.getOrgDetailsLogos(customerIds).pipe(
            map(orgDetailsList => {
              const logoMap: EntityMap<string, any> = orgDetailsList.reduce((acc, org) => {
                acc[org.orgId] = org;
                return acc;
              }, {});
              const customerKilometersWithLogos = {
                customerKilometers: {
                  customers: response.customerKilometers.customers.map(c => {
                    // If we have organization data, use the real organization name
                    const finalCustomerName = logoMap[c.customerId]?.name || c.customerName;
                    return {
                      ...c,
                      customerName: finalCustomerName,
                      logo: logoMap[c.customerId]?.logo || 'assets/images/no_profile_image.png'
                    };
                  })
                }
              };
              patchState({ customerKilometers: customerKilometersWithLogos });
              return customerKilometersWithLogos;
            })
          );
        } else {
          patchState({ customerKilometers: response });
          return of(response);
        }
      }),
      catchError(error => {
        console.error('State: Error in fetchCustomerKilometers:', error);
        return this.store.dispatch(
          new SetNotificationAction(`Failed to fetch customer kilometers: ${error.message}`, AlertDialogType.ERROR)
        );
      })
    );
  }

  // A newer Job Summary request cancels the one still in flight (tab / page / sort clicked quickly).
  @Action(JobSummaryAction, { cancelUncompleted: true })
  handleJobSummaryAction(ctx: StateContext<DashboardStateModel>, action: JobSummaryAction) {
    switch(action.status) {
      case 'loading':
        return this.handleLoadingState(ctx, action);
      case 'success':
        return this.handleSuccessState(ctx, action);
      case 'error':
        return this.handleErrorState(ctx, action);
      default:
        return;
    }
  }

  @Action(FetchCostPerKm)
  fetchCostPerKm({ patchState }: StateContext<DashboardStateModel>, { orgId, operationId, startDate, endDate, periodType }: FetchCostPerKm) {
    const input = {
      orgId,
      operationId,
      startDate,
      endDate,
      type: ValuePerType.COST_PER_KM,
      periodType: periodType || PeriodType.ONE_MONTH
    };
    return this.dashboardService.fetchTotalValuePerKmOrShipment(input).pipe(
      tap((result) => {
        patchState({ costPerKm: result });
      }),
      catchError((error) => {
        this.store.dispatch(new SetNotificationAction('Failed to fetch cost per KM data', AlertDialogType.ERROR));
        return throwError(error);
      })
    );
  }

  @Action(FetchCostPerShipment)
  fetchCostPerShipment({ patchState }: StateContext<DashboardStateModel>, { orgId, operationId, startDate, endDate, periodType }: FetchCostPerShipment) {
    const input = {
      orgId,
      operationId,
      startDate,
      endDate,
      type: ValuePerType.COST_PER_SHIPMENT,
      periodType: periodType || PeriodType.ONE_MONTH
    };
    return this.dashboardService.fetchTotalValuePerKmOrShipment(input).pipe(
      tap((result) => {
        patchState({ costPerShipment: result });
      }),
      catchError((error) => {
        this.store.dispatch(new SetNotificationAction('Failed to fetch cost per shipment data', AlertDialogType.ERROR));
        return throwError(error);
      })
    );
  }

  @Action(FetchRevenuePerKm)
  fetchRevenuePerKm({ patchState }: StateContext<DashboardStateModel>, { orgId, operationId, startDate, endDate, periodType }: FetchRevenuePerKm) {
    const input = {
      orgId,
      operationId,
      startDate,
      endDate,
      type: ValuePerType.REVENUE_PER_KM,
      periodType: periodType || PeriodType.ONE_MONTH
    };
    return this.dashboardService.fetchTotalValuePerKmOrShipment(input).pipe(
      tap((result) => {
        patchState({ revenuePerKm: result });
      }),
      catchError((error) => {
        this.store.dispatch(new SetNotificationAction('Failed to fetch revenue per KM data', AlertDialogType.ERROR));
        return throwError(error);
      })
    );
  }

  @Action(FetchRevenuePerShipment)
  fetchRevenuePerShipment({ patchState }: StateContext<DashboardStateModel>, { orgId, operationId, startDate, endDate, periodType }: FetchRevenuePerShipment) {
    const input = {
      orgId,
      operationId,
      startDate,
      endDate,
      type: ValuePerType.REVENUE_PER_SHIPMENT,
      periodType: periodType || PeriodType.ONE_MONTH
    };
    return this.dashboardService.fetchTotalValuePerKmOrShipment(input).pipe(
      tap((result) => {
        patchState({ revenuePerShipment: result });
      }),
      catchError((error) => {
        this.store.dispatch(new SetNotificationAction('Failed to fetch revenue per shipment data', AlertDialogType.ERROR));
        return throwError(error);
      })
    );
  }

  @Action(FetchProfitPerKm)
  fetchProfitPerKm({ patchState }: StateContext<DashboardStateModel>, { orgId, operationId, startDate, endDate, periodType }: FetchProfitPerKm) {
    const input = {
      orgId,
      operationId,
      startDate,
      endDate,
      type: ValuePerType.PROFIT_PER_KM,
      periodType: periodType || PeriodType.ONE_MONTH
    };
    return this.dashboardService.fetchTotalValuePerKmOrShipment(input).pipe(
      tap((result) => {
        patchState({ profitPerKm: result });
      }),
      catchError((error) => {
        this.store.dispatch(new SetNotificationAction('Failed to fetch profit per KM data', AlertDialogType.ERROR));
        return throwError(error);
      })
    );
  }

  @Action(FetchProfitPerShipment)
  fetchProfitPerShipment({ patchState }: StateContext<DashboardStateModel>, { orgId, operationId, startDate, endDate, periodType }: FetchProfitPerShipment) {
    const input = {
      orgId,
      operationId,
      startDate,
      endDate,
      type: ValuePerType.PROFIT_PER_SHIPMENT,
      periodType: periodType || PeriodType.ONE_MONTH
    };
    return this.dashboardService.fetchTotalValuePerKmOrShipment(input).pipe(
      tap((result) => {
        patchState({ profitPerShipment: result });
      }),
      catchError((error) => {
        this.store.dispatch(new SetNotificationAction('Failed to fetch profit per shipment data', AlertDialogType.ERROR));
        return throwError(error);
      })
    );
  }

  @Action(FetchSupplierCostAnalysis)
  fetchSupplierCostAnalysis({ patchState }: StateContext<DashboardStateModel>, { filter: supplierCostAnalysisFilter }: FetchSupplierCostAnalysis) {
    return this.dashboardService.fetchSupplierCostAnalysis(supplierCostAnalysisFilter).pipe(
      tap(response => {
      }),
      mergeMap(response => {
        if (response?.suppliers && response.suppliers.length > 0) {
          const supplierIds = response.suppliers.map(s => s.supplierId);

          return this.dashboardService.getOrgDetailsLogos(supplierIds).pipe(
            map(orgDetailsList => {
              const logoMap: EntityMap<string, any> = orgDetailsList.reduce((acc, org) => {
                acc[org.orgId] = org;
                return acc;
              }, {});

              const supplierCostAnalysisWithLogos = {
                ...response,
                suppliers: response.suppliers.map(s => ({
                  ...s,
                  logo: logoMap[s.supplierId]?.logo || 'assets/images/no_profile_image.png',
                  trend: s.percentageChange >= 0 ? 'up' : 'down' as 'up' | 'down'
                }))
              };

              patchState({ supplierCostAnalysis: supplierCostAnalysisWithLogos });
              return supplierCostAnalysisWithLogos;
            })
          );
        } else {
          patchState({ supplierCostAnalysis: response });
          return of(response);
        }
      }),
      catchError(error => {
        console.error('State: Error in fetchSupplierCostAnalysis:', error);
        const errorData = {
          suppliers: [],
          timePeriod: 'ERROR',
          startDate: new Date(),
          endDate: new Date(),
          previousPeriodStartDate: new Date(),
          previousPeriodEndDate: new Date()
        };
        patchState({ supplierCostAnalysis: errorData });
        return this.store.dispatch(
          new SetNotificationAction(`Failed to fetch supplier cost analysis: ${error.message}`, AlertDialogType.ERROR)
        );
      })
    );
  }


  @Action(FetchMostProfitableCustomers)
  fetchMostProfitableCustomers({ patchState }: StateContext<DashboardStateModel>, {  operationId, fromDate, toDate, pageSize }: FetchMostProfitableCustomers) {
    const {user: {orgId, operations}} = this.store.selectSnapshot(state => state.auth);
    if(operations.length < 2){
      operationId = operations[0];
    }
    return this.dashboardService.fetchMostProfitableCustomers({ orgId, operationId, fromDate, toDate, pageSize }).pipe(
      mergeMap((customers: CustomerNodeTreeMap[]) => {
        const orgIds = customers.map(c => c.customerId);
        return this.dashboardService.getOrgDetailsLogos(orgIds).pipe(
          map(orgDetailsList => {
            const logoMap: EntityMap<string, any> = orgDetailsList.reduce((acc, org) => {
              acc[org.orgId] = org;
              return acc;
            }, {});
            const customersWithLogos = customers.map(c => ({
              ...c,
              icon: logoMap[c.customerId]?.logo || 'assets/logo/default-org.png',
              name: logoMap[c.customerId]?.orgName || c.name
            })).sort((a, b) => b.totalRevenue - a.totalRevenue);
            patchState({ mostProfitableCustomers: customersWithLogos });
            return customersWithLogos;
          })
        );
      }),
      catchError(error => {
        console.error('Error fetching most profitable customers:', error);
        const mostProfitableCustomers: CustomerNodeTreeMap[] = [];
        patchState({ mostProfitableCustomers });
        return of(mostProfitableCustomers);
      })
    );
  }

  @Action(FetchVehicleUtilizationMetrics)
  fetchVehicleUtilizationMetrics(
    { patchState }: StateContext<DashboardStateModel>,
    { operationId, fromDate, toDate, periodType }: FetchVehicleUtilizationMetrics
  ) {
    const {user: {orgId, operations}} = this.store.selectSnapshot(state => state.auth);
    if(operations.length < 2){
      operationId = operations[0];
    }
    return this.dashboardService.fetchUtilisationChartData(orgId, operationId, fromDate, toDate).pipe(
      map((metrics: VehicleUtilizationMetric[]) => {
        // Sort chartData by timestamp ascending for each metric and convert timestamp to Date
        const sortedMetrics = metrics.map(metric => ({
          ...metric,
          chartData: [...metric.chartData]
            .map(d => ({
              ...d,
              timestamp: new Date(d.timestamp)
            }))
            .sort((a, b) =>
              new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
            )
        }));
        patchState({ vehicleUtilizationMetrics: sortedMetrics });
        return sortedMetrics;
      }),
      catchError(error => {
        this.store.dispatch(new SetNotificationAction('Failed to fetch vehicle utilization metrics', AlertDialogType.ERROR));
        patchState({ vehicleUtilizationMetrics: [] });
        return of([]);
      })
    );
  }

  @Action(SetVehicleUtilizationMetrics)
  setVehicleUtilizationMetrics({ patchState }: StateContext<DashboardStateModel>, { metrics }: SetVehicleUtilizationMetrics) {
    patchState({ vehicleUtilizationMetrics: metrics });
  }

  @Action(UpdateJobSummaryUIState)
  updateJobSummaryUIState({ patchState, getState }: StateContext<DashboardStateModel>, { payload }: UpdateJobSummaryUIState) {
    const state = getState();
    const updatedUIState = {
      ...state.jobSummary.uiState,
      ...payload
    };

    patchState({
      jobSummary: {
        ...state.jobSummary,
        uiState: updatedUIState
      }
    });
  }

  @Action(RestoreJobSummaryUIState)
  restoreJobSummaryUIState({ getState }: StateContext<DashboardStateModel>) {
    // This action is used to trigger restoration of UI state in the component
    // The actual restoration logic is handled in the component itself
    const state = getState();
    return state.jobSummary.uiState;
  }

  @Action(FetchSupplierList)
  fetchSupplierList({ patchState }: StateContext<DashboardStateModel>, { filter: supplierListFilter }: FetchSupplierList) {
    return this.dashboardService.fetchSupplierList(supplierListFilter).pipe(
      mergeMap(response => {
        if (response?.suppliers && response.suppliers.length > 0) {
          const supplierIds = response.suppliers.map(s => s.supplierId);
          return this.dashboardService.getOrgDetailsLogos(supplierIds).pipe(
            map(orgDetailsList => {
              const logoMap: EntityMap<string, any> = orgDetailsList.reduce((acc, org) => {
                acc[org.orgId] = org;
                return acc;
              }, {});
              const supplierListWithLogos = {
                ...response,
                suppliers: response.suppliers.map(s => ({
                  ...s,
                  supplierName: logoMap[s.supplierId]?.orgName  || 'Unknown Supplier',
                  logo: logoMap[s.supplierId]?.logo || 'assets/images/no_profile_image.png'
                }))
              };
              patchState({ supplierList: supplierListWithLogos });
              return supplierListWithLogos;
            })
          );
        } else {
          patchState({ supplierList: response });
          return of(response);
        }
      }),
      catchError(error => {
        console.error('Error fetching supplier list:', error);
        return this.store.dispatch(
          new SetNotificationAction(`Failed to fetch supplier list: ${error.message}`, AlertDialogType.ERROR)
        );
      })
    );
  }

  @Action(FetchSupplierTimeSeries)
  fetchSupplierTimeSeries({ patchState }: StateContext<DashboardStateModel>, { filter: supplierTimeSeriesFilter }: FetchSupplierTimeSeries) {
    return this.dashboardService.fetchSupplierTimeSeries(supplierTimeSeriesFilter).pipe(
      tap(response => {
        patchState({ supplierTimeSeries: response });
      }),
      catchError(error => {
        console.error('Error fetching supplier time series:', error);
        return this.store.dispatch(
          new SetNotificationAction(`Failed to fetch supplier time series: ${error.message}`, AlertDialogType.ERROR)
        );
      })
    );
  }


  @Action(FetchOnlineOfflineDeviceCount)
  fetchOnlineOfflineDeviceCount({ patchState }: StateContext<DashboardStateModel>, { orgId }: FetchOnlineOfflineDeviceCount) {
      return this.dashboardService.getAllActiveGpsDevices().pipe(
      tap(response => {
        const onlineCount = response.filter(device => device.onlineAvailability  === 'Online').length;
        const offlineCount = response.filter(device => device.onlineAvailability  === 'Offline').length;
        patchState({ onlineOfflineDeviceCount: { online: onlineCount, offline: offlineCount } });
      })
    );
  }

  getRandomInt(min: number, max: number): number {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  private handleLoadingState(ctx: StateContext<DashboardStateModel>, action: JobSummaryAction) {
    if (action.payload?.timeRange) {
      ctx.patchState({
        jobSummary: {
          ...ctx.getState().jobSummary,
          timeRange: action.payload.timeRange
        }
      });
    }

    const request = action.payload?.request;
    if (!request) {
      ctx.patchState({ jobSummary: { ...ctx.getState().jobSummary, loading: true, error: null } });
      return;
    }

    // Only the latest request may fill the widget: an older response never overwrites a newer tab.
    const key = jobSummaryRequestKey(request);
    this.jobSummaryLatestKey = key;
    this.stopJobSummaryPrefetch();

    // A page seen in the last minutes is shown at once; a fresh one needs no server call at all.
    const cached = action.payload.forceRefresh ? null : this.jobSummaryCache.get(key);
    if (cached) {
      this.applyJobSummaryPage(ctx, request, cached.value);
      if (cached.fresh) {
        this.startJobSummaryPrefetch(request);
        return;
      }
    } else if (!action.payload.forceRefresh) {
      ctx.patchState({ jobSummary: { ...ctx.getState().jobSummary, loading: true, error: null } });
    }

    if (request) {
      const {user: {orgId, id}} = this.store.selectSnapshot(state => state.auth);
      return this.dashboardService.fetchJobSummaryGraphQL(request).pipe(
        tap((result) => {
          const processedResult = this.processJobSummaryPage(result);
          if (!result.error) {
            this.jobSummaryCache.set(key, processedResult);
          }
          if (key === this.jobSummaryLatestKey) {
            this.applyJobSummaryPage(ctx, request, processedResult);
            this.startJobSummaryPrefetch(request);
          }
        }),
        catchError((error) => {
          console.error('GraphQL job summary failed, falling back to REST:', error);
          // Fallback to REST API if GraphQL fails
          return this.dashboardService.fetchAllJobSummary(orgId, request).pipe(
            tap((result) => {
              if (key === this.jobSummaryLatestKey) {
                this.applyJobSummaryPage(ctx, request, this.processJobSummaryPage(result));
              }
            })
          );
        })
      );
    }
    return;
  }

  /** Job Summary pages seen in this browser tab (see JobSummaryPageCache). */
  private readonly jobSummaryCache = new JobSummaryPageCache();
  /** Key of the request the widget is waiting for; responses for any other key only fill the cache. */
  private jobSummaryLatestKey: string | null = null;
  /** Background fetch of the other tabs' first pages; stopped by the next user request. */
  private jobSummaryPrefetch: Subscription | null = null;

  /** Completion % per row, as before, for every tab array of a response. */
  private processJobSummaryPage(result: any): any {
    return {
      ...result,
      completedJobs: this.calculateCompletionPercentageForJobs(result.completedJobs || []),
      deliveryInProgressJobs: this.calculateCompletionPercentageForJobs(result.deliveryInProgressJobs || []),
      notifiedJobs: this.calculateCompletionPercentageForJobs(result.notifiedJobs || []),
      contractedJobs: this.calculateCompletionPercentageForJobs(result.contractedJobs || []),
      plannedJobs: this.calculateCompletionPercentageForJobs(result.plannedJobs || []),
      loadAssignmentJobs: this.calculateCompletionPercentageForJobs(result.loadAssignmentJobs || []),
      pendingAcceptanceJobs: this.calculateCompletionPercentageForJobs(result.pendingAcceptanceJobs || []),
      acceptedJobs: this.calculateCompletionPercentageForJobs(result.acceptedJobs || []),
      terminatedJobs: this.calculateCompletionPercentageForJobs(result.terminatedJobs || [])
    };
  }

  /** Puts a (processed) page in the widget. */
  private applyJobSummaryPage(ctx: StateContext<DashboardStateModel>, request: JobSummaryRequest, page: any): void {
    const pagination = page.pagination || {
      totalItems: 0,
      itemsPerPage: request.pageSize,
      currentPage: request.pageIndex,
      totalPages: 0
    };
    ctx.patchState({
      jobSummary: {
        ...ctx.getState().jobSummary,
        completedJobs: page.completedJobs,
        deliveryInProgressJobs: page.deliveryInProgressJobs,
        notifiedJobs: page.notifiedJobs,
        contractedJobs: page.contractedJobs,
        plannedJobs: page.plannedJobs,
        loadAssignmentJobs: page.loadAssignmentJobs,
        pendingAcceptanceJobs: page.pendingAcceptanceJobs,
        acceptedJobs: page.acceptedJobs,
        terminatedJobs: page.terminatedJobs,
        timeZone: page.timeZone ?? null,
        pagination,
        loading: false,
        selectedStatus: request.status
      }
    });
  }

  /**
   * After a tab is shown, quietly fetch the first page of every other tab (same org, operation and
   * period), one at a time, into the cache - so the next tab click shows at once. Pages already fresh
   * are skipped; the next user request stops it.
   */
  private startJobSummaryPrefetch(request: JobSummaryRequest): void {
    this.stopJobSummaryPrefetch();
    const pending = otherTabFirstPages(request).filter(next => !this.jobSummaryCache.isFresh(jobSummaryRequestKey(next)));
    if (pending.length === 0) {
      return;
    }
    this.jobSummaryPrefetch = timer(JOB_SUMMARY_PREFETCH_DELAY_MS).pipe(
      concatMap(() => from(pending)),
      concatMap(next => this.dashboardService.fetchJobSummaryGraphQL(next).pipe(
        tap(result => {
          if (!result.error) {
            this.jobSummaryCache.set(jobSummaryRequestKey(next), this.processJobSummaryPage(result));
          }
        }),
        catchError(() => EMPTY)
      ))
    ).subscribe();
  }

  private stopJobSummaryPrefetch(): void {
    if (this.jobSummaryPrefetch && !this.jobSummaryPrefetch.closed) {
      this.jobSummaryPrefetch.unsubscribe();
    }
    this.jobSummaryPrefetch = null;
  }

  private handleSuccessState(ctx: StateContext<DashboardStateModel>, action: JobSummaryAction) {
    if (action.payload?.data) {
      const {user: {orgId, id}} = this.store.selectSnapshot(state => state.auth);
      return this.dashboardService.fetchJobSummaryByStatus(orgId, action.payload.request).pipe(
        tap((result) => {
          // Calculate completion percentage for all jobs when loaded
          const processedResult = {
            ...result,
            completedJobs: this.calculateCompletionPercentageForJobs(result.completedJobs || []),
            deliveryInProgressJobs: this.calculateCompletionPercentageForJobs(result.deliveryInProgressJobs || []),
            notifiedJobs: this.calculateCompletionPercentageForJobs(result.notifiedJobs || []),
            contractedJobs: this.calculateCompletionPercentageForJobs(result.contractedJobs || []),
            plannedJobs: this.calculateCompletionPercentageForJobs(result.plannedJobs || []),
            loadAssignmentJobs: this.calculateCompletionPercentageForJobs(result.loadAssignmentJobs || []),
            pendingAcceptanceJobs: this.calculateCompletionPercentageForJobs(result.pendingAcceptanceJobs || []),
            acceptedJobs: this.calculateCompletionPercentageForJobs(result.acceptedJobs || []),
            terminatedJobs: this.calculateCompletionPercentageForJobs(result.terminatedJobs || [])
          };
          ctx.patchState({
            jobSummary: {
              ...ctx.getState().jobSummary,
              completedJobs: processedResult.completedJobs,
              deliveryInProgressJobs: processedResult.deliveryInProgressJobs,
              notifiedJobs: processedResult.notifiedJobs,
              contractedJobs: processedResult.contractedJobs,
              plannedJobs: processedResult.plannedJobs,
              loadAssignmentJobs: processedResult.loadAssignmentJobs,
              pendingAcceptanceJobs: processedResult.pendingAcceptanceJobs,
              acceptedJobs: processedResult.acceptedJobs,
              terminatedJobs: processedResult.terminatedJobs,
              timeZone: result.timeZone ?? null,
              pagination: result.pagination || ctx.getState().jobSummary.pagination,
              loading: false
            }
          });
        })
      );
    }
  }

  private handleErrorState(ctx: StateContext<DashboardStateModel>, action: JobSummaryAction) {
    return of(null).pipe(
      tap(() => {
        ctx.patchState({
          jobSummary: {
            ...ctx.getState().jobSummary,
            loading: false,
            error: action.payload?.error || 'Unknown error'
          }
        });
      })
    );
  }

  private updateJobWithWorkOrders(patchState: any, getState: any, jobRefId: string, workOrders: any[]) {
    const state = getState();
    const updateJobsWithWorkOrders = (jobs: any[]) =>
      jobs.map(job => {
        if (job.jobReferenceId === jobRefId) {
          return {
            ...job,
            allWorkOrders: workOrders,
            originalExpectedEndTime: job.originalExpectedEndTime || job.expectedEndTime
          };
        }
        return job;
      });

    patchState({
      jobSummary: {
        ...state.jobSummary,
        completedJobs: updateJobsWithWorkOrders(state.jobSummary.completedJobs),
        deliveryInProgressJobs: updateJobsWithWorkOrders(state.jobSummary.deliveryInProgressJobs),
        notifiedJobs: updateJobsWithWorkOrders(state.jobSummary.notifiedJobs),
        contractedJobs: updateJobsWithWorkOrders(state.jobSummary.contractedJobs),
        plannedJobs: updateJobsWithWorkOrders(state.jobSummary.plannedJobs),
        loadAssignmentJobs: updateJobsWithWorkOrders(state.jobSummary.loadAssignmentJobs || []),
        pendingAcceptanceJobs: updateJobsWithWorkOrders(state.jobSummary.pendingAcceptanceJobs || []),
        acceptedJobs: updateJobsWithWorkOrders(state.jobSummary.acceptedJobs || []),
        terminatedJobs: updateJobsWithWorkOrders(state.jobSummary.terminatedJobs || [])
      }
    });
  }

  private updateJobWorkOrderFromNotification(patchState: any, getState: any, jobRefId: string, updatedWorkOrder: any) {
    const state = getState();
    const updateJobsWithWorkOrders = (jobs: any[]) =>
      jobs.map(job => {
        if (job.jobReferenceId === jobRefId) {
          const existingWorkOrders = job.allWorkOrders || [];
          const workOrderIndex = existingWorkOrders.findIndex(wo => wo.workOrderId === updatedWorkOrder.workOrderId);

          let updatedWorkOrders;
          if (workOrderIndex >= 0) {
            updatedWorkOrders = [...existingWorkOrders];
            updatedWorkOrders[workOrderIndex] = { ...existingWorkOrders[workOrderIndex], ...updatedWorkOrder };
          } else {
            updatedWorkOrders = [...existingWorkOrders, updatedWorkOrder];
          }

          return { ...job, allWorkOrders: updatedWorkOrders };
        }
        return job;
      });

    // patchState({
    //   jobSummary: {
    //     ...state.jobSummary,
    //     completedJobs: updateJobsWithWorkOrders(state.jobSummary.completedJobs),
    //     deliveryInProgressJobs: updateJobsWithWorkOrders(state.jobSummary.deliveryInProgressJobs),
    //     notifiedJobs: updateJobsWithWorkOrders(state.jobSummary.notifiedJobs),
    //     contractedJobs: updateJobsWithWorkOrders(state.jobSummary.contractedJobs),
    //     plannedJobs: updateJobsWithWorkOrders(state.jobSummary.plannedJobs)
    //   }
    // });
  }

  private updateJobMetrics(patchState: any, getState: any, jobRefId: string) {
    const state = getState();

    const calculateJobMetrics = (job: any) => {
      if (!job.allWorkOrders || job.allWorkOrders.length === 0) {
        return job;
      }

      // Calculate completion percentage - match backend logic: COMPLETED, CANCEL, DEFERRED states count as completed
      const completedWorkOrders = job.allWorkOrders.filter(wo =>
        (wo.state === 'COMPLETED' || wo.state === 'CANCEL' || wo.state === 'DEFERRED') &&
        wo.workOrderId !== 'VEHICLE-CHECK-LIST-LO' &&
        wo.workOrderId !== 'SOFT-START'
      );
      const totalWorkOrders = job.allWorkOrders.filter(wo =>
        wo.workOrderId !== 'VEHICLE-CHECK-LIST-LO' &&
        wo.workOrderId !== 'SOFT-START'
      ).length;
      const completionPercentage = totalWorkOrders > 0
        ? Math.round(((completedWorkOrders.length) / (totalWorkOrders)) * 100)
        : 0;

      const updatedJob = {
        ...job,
        completionPercentage,
        // Keep the original expectedEndTime from the network response
        expectedEndTime: job.expectedEndTime || job.originalExpectedEndTime
      };

      return updatedJob;
    };

    // Helper function to update jobs array
    const updateJobsArray = (jobs: any[]) =>
      jobs.map(job => {
        if (job.jobReferenceId === jobRefId) {
          return calculateJobMetrics(job);
        }
        return job;
      });

    // Update all job types
    patchState({
      jobSummary: {
        ...state.jobSummary,
        completedJobs: updateJobsArray(state.jobSummary.completedJobs),
        deliveryInProgressJobs: updateJobsArray(state.jobSummary.deliveryInProgressJobs),
        notifiedJobs: updateJobsArray(state.jobSummary.notifiedJobs),
        contractedJobs: updateJobsArray(state.jobSummary.contractedJobs),
        plannedJobs: updateJobsArray(state.jobSummary.plannedJobs),
        loadAssignmentJobs: updateJobsArray(state.jobSummary.loadAssignmentJobs || []),
        pendingAcceptanceJobs: updateJobsArray(state.jobSummary.pendingAcceptanceJobs || []),
        acceptedJobs: updateJobsArray(state.jobSummary.acceptedJobs || []),
        terminatedJobs: updateJobsArray(state.jobSummary.terminatedJobs || [])
      }
    });
  }

  // Helper method to calculate completion percentage for an array of jobs
  private calculateCompletionPercentageForJobs(jobs: any[]): any[] {
    return jobs.map(job => {
      if (job.completionPercentage !== undefined && job.completionPercentage !== null) {
        return {
          ...job,
          originalExpectedEndTime: job.originalExpectedEndTime || job.expectedEndTime
        };
      }

      if (!job.allWorkOrders || job.allWorkOrders.length === 0) {
        return { ...job, completionPercentage: 0 };
      }

      // Calculate completion percentage - match backend logic: COMPLETED, CANCEL, DEFERRED states count as completed
      const completedWorkOrders = job.allWorkOrders.filter(wo =>
        (wo.state === 'COMPLETED' || wo.state === 'CANCEL' || wo.state === 'DEFERRED') &&
        wo.workOrderId !== 'VEHICLE-CHECK-LIST-LO' &&
        wo.workOrderId !== 'SOFT-START'
      );
      const totalWorkOrders = job.allWorkOrders.filter(wo =>
        wo.workOrderId !== 'VEHICLE-CHECK-LIST-LO' &&
        wo.workOrderId !== 'SOFT-START'
      ).length;

      const completionPercentage = totalWorkOrders > 0
        ? Math.round(((completedWorkOrders.length) / (totalWorkOrders)) * 100)
        : 0;

      return {
        ...job,
        completionPercentage,
        // Store the original expectedEndTime if not already stored
        originalExpectedEndTime: job.originalExpectedEndTime || job.expectedEndTime
      };
    });
  }

  @Action(FetchTimeInsightsData)
  fetchTimeInsightsData({ patchState, dispatch }: StateContext<DashboardStateModel>, action: FetchTimeInsightsData) {
    const input = {
      orgId: action.orgId,
      operationId: action.operationId,
      startDate: action.startDate,
      endDate: action.endDate,
      periodType: action.periodType
    };

    return this.dashboardService.fetchTimeInsightsData(input).pipe(
      map((result: TimeInsightsResult) => {
        patchState({
          revenueFinancialData: result.financial.revenue,
          costFinancialData: result.financial.cost,
          profitFinancialData: result.financial.profit,
          costPerKm: result.metrics.costPerKm,
          costPerShipment: result.metrics.costPerShipment,
          revenuePerKm: result.metrics.revenuePerKm,
          revenuePerShipment: result.metrics.revenuePerShipment,
          profitPerKm: result.metrics.profitPerKm,
          profitPerShipment: result.metrics.profitPerShipment,
          lastUpdated: new Date(),
          executionMetrics: result.metadata
        });
        
        return result;
      }),
      catchError((error) => {
        console.error('Error fetching time insights data:', error);
        return dispatch(new SetNotificationAction(
          'Failed to fetch time insights data. Please try again.', 
          AlertDialogType.ERROR
        ));
      })
    );
  }

  @Action(FetchBehalfPartnerOrganizations)
  fetchBehalfPartnerOrganizations(
    { patchState, dispatch }: StateContext<DashboardStateModel>,
    { pageNum, pageSize, searchKey }: FetchBehalfPartnerOrganizations
  ) {
    return this.dashboardService.getPartnerOrganizationsForBehalf(pageNum, pageSize, searchKey).pipe(
      tap((page: { content: {id: string, orgName: string}[] }) => {
        patchState({ partnerOrganizations: page.content || [] });
      }),
      catchError((error) => {
        console.error('Error fetching partner organizations:', error);
        return dispatch(new SetNotificationAction(
          'Failed to fetch partner organizations',
          AlertDialogType.ERROR
        ));
      })
    );
  }

  @Action(FetchBehalfOnboardedOrganizations)
  fetchBehalfOnboardedOrganizations(
    { patchState, dispatch }: StateContext<DashboardStateModel>,
    { pageNum, pageSize, searchKey }: FetchBehalfOnboardedOrganizations
  ) {
    return this.dashboardService.getOnBoardedOrganizationsForBehalf(pageNum, pageSize, searchKey).pipe(
      tap((page: { content: {id: string, orgName: string}[] }) => {
        patchState({ onboardedOrganizations: page.content || [] });
      }),
      catchError((error) => {
        console.error('Error fetching onboarded organizations:', error);
        return dispatch(new SetNotificationAction(
          'Failed to fetch onboarded organizations',
          AlertDialogType.ERROR
        ));
      })
    );
  }

  @Action(FetchBehalfOrgDetails)
  fetchBehalfOrgDetails(
    { patchState, getState }: StateContext<DashboardStateModel>,
    { orgId }: FetchBehalfOrgDetails
  ) {
    const state = getState();
    
    // Check if already fetched
    if (state.behalfOrgDetails[orgId]) {
      return;
    }

    return this.dashboardService.getOrgDetails([orgId]).pipe(
      tap((orgDetails: OrgDetails[]) => {
        if (orgDetails && orgDetails.length > 0) {
          const details = orgDetails[0];
          patchState({
            behalfOrgDetails: {
              ...state.behalfOrgDetails,
              [orgId]: {
                logo: details.logo,
                orgName: details.orgName
              }
            }
          });
        }
      }),
      catchError((error) => {
        console.error('Error fetching behalf org details:', error);
        return of(null);
      })
    );
  }
}
